import axios from 'axios';
import _ from 'lodash';
import {push} from 'connected-react-router';
import {SubmissionError, change, submit, touch} from 'redux-form';
import {
    DOUBLE_CONSENT_AGREE,
    DOUBLE_CONSENT_HIDE,
    DOUBLE_CONSENT_SHOW
} from './TermsPageConstants';
import setUrl from '../../../framework/App/SetUrl';
import {NETWORK, FORM_ID} from '../../../framework/App/AppConstants';
import {PAGE_NAME} from '../../config/pageNames';
import {SUBMIT_API} from '../../config/apiEndpoints';
import {setServerData} from '../../../framework/store/ServerData/ServerDataActions';
import {spinnerClose} from '../../../framework/components/FullPageSpinner/FullPageSpinnerActions';
import { buildCapeTxnRunner } from '../../../framework/App/CapeTransaction';
import { handleServerSideErrors } from '../../utils/USUtils';
import { uniqueTimestampId } from '../../../framework/App/AppUtils';
import { safeStringify } from '../../../shared-utilities/safeStringify';

const runUsApplyCapeTxn = buildCapeTxnRunner('us_apply');

export const doubleConsentShow = () => ({
    type: DOUBLE_CONSENT_SHOW
});

export const doubleConsentClose = () => ({
    type: DOUBLE_CONSENT_HIDE
});

export const doubleConsentAgree = () => ({
    type: DOUBLE_CONSENT_AGREE
});

export const handleRedirect = (response, serverData) => {
    if (response && _.get(response, 'data.redirect')) {
        setUrl(window, response.data.redirect, serverData);
        return true;
    }
    return false;
};
class ResponseDataError extends Error {
    constructor(message, response) {
        super(message);

        this.response = response;
    }
}

const executeSubmitApplicationAxios = (data, csrf) => {
    return axios.request({
        url: SUBMIT_API,
        method: 'post',
        headers: {
            ...NETWORK.REQUEST.XHR.HEADER,
            'X-CSRF-Token': csrf
        },
        data
    });
};

const executeSubmitApplicationFetch = (data, csrf, nsid) => {
    let submitApiEndpoint = SUBMIT_API;

    if (nsid) {
        // Need to manually pass NSID for cookieless sessions when using Fetch API.
        // This is handled in a global interceptor for Axios.
        submitApiEndpoint = `${submitApiEndpoint}?nsid=${nsid}`;
    }
    
    return fetch(submitApiEndpoint, {
        method: 'POST',
        headers: {
            ...NETWORK.REQUEST.XHR.HEADER,
            'X-CSRF-Token': csrf
        },
        body: safeStringify(data)
    })
        .then(response => response.json().then(data => ({ ok: response.ok, status: response.status, data })))
        .then(response => {
            if (response.ok) {
                return response;
            }

            return Promise.reject(new ResponseDataError(`Submit application failed with non-success status code: ${response.status}`, response));
        });
};

const executeSubmitApplication = (data, serverData) => {
    const csrf = _.get(serverData, '_csrf', '');
    const nsid = _.get(serverData, 'nsid');
    const submitApplicationFetchEnabled = _.get(serverData, 'featureFlags.submitApplicationFetchEnabled', false);

    capeLog && capeLog(`executing submit application: submitApplicationFetchEnabled=${submitApplicationFetchEnabled}`);
    
    if (submitApplicationFetchEnabled) {
        return executeSubmitApplicationFetch(data, csrf, nsid);
    }

    return executeSubmitApplicationAxios(data, csrf);
};

export const submitApplicationApi = data => (dispatch, getState) => {
    return runUsApplyCapeTxn(() => {
        data.clientRequestId = uniqueTimestampId();
        const {serverData = {}} = getState();

        return executeSubmitApplication(data, serverData)
            .then(async response => {
                capeLog && capeLog(`completed submit application request ${uniqueTimestampId()}`);

                // handle server-side errors
                await handleServerSideErrors(dispatch, getState, response);
                //If there are any validation errors we want to send user back to page with error
                if (_.get(response, 'data.validationErrors')) {
                    const pageName = _.get(response, 'data.pageName');
                    const validationErrors = _.get(response, 'data.validationErrors');
                    capeLog && capeLog(`response:Validation error on :${pageName}`);

                    dispatch(setServerData({validationErrorModel: validationErrors}));
                    dispatch(doubleConsentClose());
                    for (const validationError of validationErrors) {
                        dispatch(change(FORM_ID, validationError.field, ''));
                        dispatch(touch(FORM_ID, validationError.field));
                    }
                    dispatch(push(pageName));
                    dispatch(spinnerClose());
                } else if (_.get(response, 'data.workflowId')) {
                    dispatch(setServerData({
                        appPendData: {
                            ...response.data
                        }
                    }));
                    dispatch(push(PAGE_NAME.APP_PEND_PAGE));
                    dispatch(spinnerClose());
                } else if (handleRedirect(response, serverData)) {
                    return;
                } else {
                    dispatch(setServerData({
                        decisionModel: response.data,
                        userLoggedIn: _.get(response, 'data.userLoggedIn')
                    }));

                    dispatch(push(PAGE_NAME.DECISION_PAGE));
                    dispatch(spinnerClose());
                }
            })
            .catch(error => {
                // show server-side errors on form
                if (error instanceof SubmissionError) {
                    dispatch(doubleConsentClose());
                    return Promise.reject(error);
                }
                capeLog && capeLog('response:error, TermsPageActions,' + (error && error.message));

                if (handleRedirect(error && error.response, serverData)) {
                    return;
                }

                if (_.get(error, 'response.data.err', false)) {
                    dispatch(setServerData({errorModel: _.get(error, 'response.data', {})}));
                    dispatch(push(PAGE_NAME.ERROR_PAGE));
                    dispatch(spinnerClose());
                } else {
                    // should never be here
                    capeLog && capeLog('response:error: unexpected error response for TermsPage submit');
                    setUrl(window, PAGE_NAME.ERROR_PAGE, serverData);
                }
            });
    });
};

export const overrideTermsStyles = () => {
    const css = '.ppcTerms-position { width: 100% !important; max-width: 100% !important; margin: 0 !important; padding: 0 0.35rem !important; box-sizing: border-box; }';
    const iframe = document.getElementById('terms-and-conditions-iframe');

    if (_.get(iframe, 'contentWindow.document.head')) {
        const style = document.createElement('style');
        style.type = 'text/css';
        style.appendChild(document.createTextNode(css));
        iframe.contentWindow.document.head.appendChild(style);
    }
};
